import React from "react";

import type { StripeCard as StripeCardType } from "../../../__generated__/gql/graphql";
import { StripeCard } from "../stripe-card";
import StripeCardSetDialog from "../stripe-card-set-dialog";
import type { StripeCardSetDialogMessages } from "../stripe-card-set-dialog";

export type CreditcardInfoLayoutType = "horizontal" | "vertical" | "simple";

export type CreditcardInfoMessages = {
  registerPaymentMethod: string;
  registerCreditCard: string;
  paymentMethodRegistration: string;
  changePaymentMethod: string;
  dialogMessages?: StripeCardSetDialogMessages;
};

const JA_MESSAGES: CreditcardInfoMessages = {
  registerPaymentMethod: "お支払い方法を登録する",
  registerCreditCard: "クレジットカード登録",
  paymentMethodRegistration: "お支払い方法の登録",
  changePaymentMethod: "お支払い方法の変更",
};

function EmptyCardInfo({
  organizationId,
  variant = "vertical",
  messages,
}: {
  organizationId?: string;
  variant?: CreditcardInfoLayoutType;
  messages: CreditcardInfoMessages;
}) {
  switch (variant) {
    case "vertical":
      return (
        <div className="flex flex-col items-center p-[30px]">
          <div className="mb-[40px] self-stretch text-left text-[14px] leading-none font-bold md:text-center">
            {messages.registerPaymentMethod}
          </div>
          <StripeCardSetDialog
            organizationId={organizationId}
            messages={messages.dialogMessages}
          >
            <button
              data-analytics-id="payment_method_opened_register"
              className="btn-primary w-full max-w-[280px] px-[16px] py-[12px] whitespace-normal"
            >
              {messages.registerCreditCard}
            </button>
          </StripeCardSetDialog>
        </div>
      );
    case "horizontal":
      return (
        <div className="flex flex-col items-center space-y-[16px] p-[30px] md:flex-row md:space-y-0 md:space-x-[30px]">
          <div className="self-stretch text-left text-[14px] font-bold md:basis-1/2">
            {messages.registerPaymentMethod}
          </div>
          <div className="w-full md:basis-1/2">
            <StripeCardSetDialog
              organizationId={organizationId}
              messages={messages.dialogMessages}
            >
              <button
                data-analytics-id="payment_method_opened_register"
                className="btn-primary w-full px-[16px] py-[12px] whitespace-normal"
              >
                {messages.registerCreditCard}
              </button>
            </StripeCardSetDialog>
          </div>
        </div>
      );
    case "simple":
      return (
        <div className="flex flex-col items-center space-y-[16px] p-[30px] md:flex-row md:space-y-0 md:space-x-[30px]">
          <div className="mx-auto w-full max-w-[255px]">
            <StripeCardSetDialog
              organizationId={organizationId}
              messages={messages.dialogMessages}
            >
              <button
                data-analytics-id="payment_method_opened_register"
                className="btn-primary w-full px-[16px] py-[12px] whitespace-normal"
              >
                {messages.registerCreditCard}
              </button>
            </StripeCardSetDialog>
          </div>
        </div>
      );
  }
}

function FilledCardInfo({
  card,
  organizationId,
  variant = "vertical",
  messages,
}: {
  card: NonNullable<StripeCardType>;
  organizationId?: string;
  variant?: CreditcardInfoLayoutType;
  messages: CreditcardInfoMessages;
}) {
  switch (variant) {
    case "simple":
      return (
        <div className="flex flex-col gap-[16px] p-[30px] md:flex-row md:items-center md:justify-between">
          <div className="flex-1">
            <StripeCard card={card} />
          </div>
          <div className="shrink-0">
            <StripeCardSetDialog
              organizationId={organizationId}
              messages={messages.dialogMessages}
            >
              <button
                data-analytics-id="payment_method_opened_change"
                className="old-btn-small-white mt-[10px] min-w-[140px] px-[12px] py-[8px] whitespace-normal md:mt-0"
              >
                {messages.changePaymentMethod}
              </button>
            </StripeCardSetDialog>
          </div>
        </div>
      );
    default:
      return (
        <div className="p-[30px]">
          <h2 className="mb-[20px] text-[14px] leading-none font-bold">
            {messages.paymentMethodRegistration}
          </h2>
          <div className="flex flex-col gap-[16px] md:flex-row md:items-start md:justify-between">
            <div className="flex-1">
              <StripeCard card={card} />
            </div>
            <div className="shrink-0">
              <StripeCardSetDialog
                organizationId={organizationId}
                messages={messages.dialogMessages}
              >
                <button
                  data-analytics-id="payment_method_opened_change"
                  className="old-btn-small-white mt-[10px] min-w-[140px] px-[12px] py-[8px] whitespace-normal md:mt-0"
                >
                  {messages.changePaymentMethod}
                </button>
              </StripeCardSetDialog>
            </div>
          </div>
        </div>
      );
  }
}

/**
 * クレジットカードの登録・情報表示・変更
 */
export function CreditcardInfo({
  card,
  organizationId,
  variant,
  className,
  messages = JA_MESSAGES,
}: {
  card: StripeCardType | null | undefined;
  organizationId?: string;
  variant?: CreditcardInfoLayoutType;
  className?: string;
  messages?: CreditcardInfoMessages;
}) {
  return (
    <div className={className}>
      {card ? (
        <FilledCardInfo
          card={card}
          organizationId={organizationId}
          variant={variant}
          messages={messages}
        />
      ) : (
        <EmptyCardInfo
          organizationId={organizationId}
          variant={variant}
          messages={messages}
        />
      )}
    </div>
  );
}
